"use client";

import { StrictExclude } from "@faire/web--source/common/typescriptUtils";
import * as React from "react";
import {
  Route as ReactRoute,
  RouteComponentProps,
  RouteProps,
  useLocation,
} from "react-router-dom";

import { removeLocaleFromUrl } from "./util";

export type RouteRenderFn = (
  props: RouteComponentProps<Partial<Record<string, string>>>
) => React.ReactNode;

export type UnattributedRoutePropsWithoutRenderFn = Omit<RouteProps, "render">;

export type UnattributedRouteProps<
  T extends UnattributedRoutePropsWithoutRenderFn,
  R extends RouteRenderFn,
> = T & {
  /** A more strongly-typed version of react-router-dom's Route render prop that prevents passing a React component as render prop which results in runtime errors */
  render?: StrictExclude<R, React.FC>;
};

export const Route = <
  T extends UnattributedRoutePropsWithoutRenderFn,
  R extends RouteRenderFn,
>(
  props: UnattributedRouteProps<T, R>
) => {
  const currentLocation = useLocation();
  const { location: overriddenLocation } = props;

  const augmentedLocation = React.useMemo(() => {
    const newLocation = {
      ...currentLocation,
      ...overriddenLocation,
    };
    newLocation.pathname = removeLocaleFromUrl(newLocation.pathname);
    return newLocation;
  }, [currentLocation, overriddenLocation]);

  const augmentedPath = React.useMemo(
    () => (props.path ? removeLocaleFromUrl(`${props.path}`) : ""),
    [props.path]
  );

  if (!augmentedPath) {
    return <ReactRoute {...props} location={augmentedLocation} />;
  }

  return (
    <ReactRoute {...props} path={augmentedPath} location={augmentedLocation} />
  );
};
